import { TrackingPromotionCreative } from '@aboutyou/legacy-tracking-sdk'
import { Environment } from 'helpers/environment'
import { cdnImageUrl } from 'helpers/image'
import { useRouter } from 'helpers/navigation'
import { buildStoryRowImageAlt } from 'helpers/seo'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { GenericPromotionTracker } from 'helpers/tracking/trackerComponents/ElementTracker'
import { cdnVideoUrl } from 'helpers/video'
import React, { FC } from 'react'
import { getLinkFromStoryElement } from 'services/providers/iris/entities/stories/storyRows/storyRow.normalizer.helpers'
import {
  NormalizedStoryElement,
  NormalizedStoryImageElement,
} from 'services/providers/iris/entities/stories/storyRows/storyRow.normalizer.types'
import { IStorySEO } from 'services/providers/iris/normalizers/seo'
import ImageTile from 'uiComponents/atoms/Tiles/ImageTile'
import VideoTile from 'uiComponents/atoms/Tiles/VideoTile'
import EditorialHeadline from 'uiComponents/molecules/EditorialHeadline/EditorialHeadline'
import StoryRowHeadline from 'uiComponents/molecules/EditorialHeadline/StoryRowHeadline'
import EditorialOutfitReco from 'uiComponents/organisms/EditorialOutfitReco/EditorialOutfitReco'
import { DEFAULT_IMAGE_SIZE } from './EditorialRow'

const getEditorialTrackingCreative = (
  element: NormalizedStoryImageElement,
  linkTarget: string | null,
) => {
  switch (element.type) {
    case 'image':
      return linkTarget
        ? TrackingPromotionCreative.Editorial
        : TrackingPromotionCreative.EditorialImageNoDestination
    case 'video':
      return linkTarget
        ? TrackingPromotionCreative.EditorialVideo
        : TrackingPromotionCreative.EditorialVideoNoDestination
  }
}

const LinkTile: FC<{
  element: NormalizedStoryImageElement
  children?: React.ReactNode
}> = ({ element, children }) => {
  const { navigateClient } = useRouter()
  const linkTarget = getLinkFromStoryElement(element)
  const creative = getEditorialTrackingCreative(element, linkTarget)

  const trackingProps = {
    id: element.id,
    name: element.type,
    resourceId: element.linkedResource ? element.linkedResource.id : undefined,
    resourceType: element.linkedResource
      ? element.linkedResource.entityType
      : undefined,
    cachePageImpression: true,
    source: creative,
  }

  return linkTarget ? (
    <ElementTrackingContextProvider>
      <GenericPromotionTracker<HTMLAnchorElement>
        url={linkTarget}
        {...trackingProps}
      >
        {({ onClickCapture, ref }) => (
          <a
            href={linkTarget}
            onClickCapture={onClickCapture}
            ref={ref}
            onClick={navigateClient(linkTarget)}
          >
            {children}
          </a>
        )}
      </GenericPromotionTracker>
    </ElementTrackingContextProvider>
  ) : (
    <ElementTrackingContextProvider>
      {children}
      <GenericPromotionTracker {...trackingProps} />
    </ElementTrackingContextProvider>
  )
}

export interface ContentConfig {
  imgSize?: number
  imgAlt?: string
}

const getImageSrc = (
  element: NormalizedStoryImageElement,
  config: ContentConfig,
) => {
  const imgHash = element.image!.hash
  const size = config.imgSize || DEFAULT_IMAGE_SIZE

  const src = cdnImageUrl(imgHash, {
    width: size,
  })
  const srcRetina = cdnImageUrl(imgHash, {
    width: size * 2,
  })

  return { src, srcSet: `${src} 1x, ${srcRetina} 2x` }
}

export interface ContentNode {
  type: 'image' | 'video' | 'text' | 'outfit'
  node: React.ReactNode
}

const elementToCommonContent = (
  element: NormalizedStoryElement,
  environment: Environment,
  config: ContentConfig = {},
  seo: IStorySEO,
): ContentNode => {
  switch (element.type) {
    case 'video': {
      return {
        type: 'video' as const,
        node: (
          <LinkTile element={element}>
            <VideoTile
              src={
                cdnVideoUrl({
                  pathname: element.video ? element.video.url : null,
                  environment,
                })!
              }
              previewSrc={
                element.image ? cdnImageUrl(element.image.hash) : undefined
              }
            />
          </LinkTile>
        ),
      }
    }
    case 'image': {
      const { src, srcSet } = getImageSrc(element, config)
      return {
        type: 'image' as const,
        node: (
          <LinkTile element={element}>
            <ImageTile
              src={src}
              srcSet={srcSet}
              alt={
                buildStoryRowImageAlt(element.linkedResource) ||
                seo?.genericImgAlt
              }
            />
          </LinkTile>
        ),
      }
    }
    case 'outfit': {
      return {
        type: 'outfit' as const,
        node: <EditorialOutfitReco outfitId={element.outfitId} />,
      }
    }
    default: {
      return {
        type: element.type,
        node: null,
      }
    }
  }
}

const elementToSingleTileContent = (
  element: NormalizedStoryElement,
  environment: Environment,
  config: ContentConfig = {},
  seo: IStorySEO,
) => {
  switch (element.type) {
    case 'text': {
      return {
        type: 'text' as const,
        node: <EditorialHeadline text={element.text!} title={element.title!} />,
      }
    }
    case 'outfit': {
      return {
        type: 'outfit' as const,
        node: <EditorialOutfitReco outfitId={element.outfitId} />,
      }
    }
    default:
      return elementToCommonContent(element, environment, config, seo)
  }
}

const elementToDoubleTileContent = (
  element: NormalizedStoryElement,
  environment: Environment,
  config: ContentConfig = {},
  seo: IStorySEO,
) => {
  switch (element.type) {
    case 'text': {
      return {
        type: 'text' as const,
        node: <StoryRowHeadline text={element.text!} title={element.title!} />,
      }
    }
    default:
      return elementToCommonContent(element, environment, config, seo)
  }
}

export const elementToContent = ({
  kind,
  element,
  environment,
  config,
  seo,
}: {
  kind: 'doubleTile' | 'singleTile'
  element: NormalizedStoryElement
  environment: Environment
  config?: ContentConfig
  seo: IStorySEO
}): ContentNode => {
  switch (kind) {
    case 'singleTile':
      return elementToSingleTileContent(element, environment, config, seo)
    case 'doubleTile':
      return elementToDoubleTileContent(element, environment, config, seo)
    default:
      return {
        type: element.type,
        node: null,
      }
  }
}
